import type { College } from '@backrs_ui/common-ui';

import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { getExtracurricularsLabel, getRigorLabel, getSelectivityLabel } from '../college.util';

const WHO_GETS_IN_STRINGS = appStrings.modules.college.whoGetsIn;

export const useAgentContext = (college: College) => {
  useRegisterAgentContext({
    kind: 'section',
    title: 'Who Gets In',
    description:
      'Admissions data including selectivity, acceptance rate, deadlines, test scores, GPA, rigor, and extracurricular expectations',
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Acceptance Rate',
    display: 'Line item with pie chart icon showing acceptance percentage',
    detail: WHO_GETS_IN_STRINGS.charts.acceptanceRate.tooltip,
    data: { acceptance_rate: college.admission_rate },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Deadlines',
    display: 'Calendar icon line items, only shown if a deadline exists',
    data: {
      early_decision_deadline: college.early_decision_deadline,
      early_action_deadline: college.early_action_deadline,
      early_action_restrictive: college.early_action_restrictive,
      application_deadline: college.application_deadline,
      rolling_admission: college.rolling_admission,
      notification_date: college.notification_date,
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Application Requirements',
    display:
      'Line items for test-optional, application fee, and essay / recommendation / interview requirements, only shown when known',
    data: {
      test_optional: college.test_optional,
      application_fee: college.application_fee,
      application_fee_waiver: college.application_fee_waiver,
      essay_requirement: college.essay_requirement,
      recommendation_requirement: college.recommendation_requirement,
      interview_requirement: college.interview_requirement,
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'High School GPA',
    display: 'Horizontal range bar chart (scale 0–4.5) showing 25th, 50th, 75th percentile',
    detail: WHO_GETS_IN_STRINGS.charts.highSchoolGPA.tooltip,
    data: { p25: college.gpa_25, p50: college.gpa_50, p75: college.gpa_75 },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'SAT Score',
    display:
      'Horizontal range bar chart (scale 400–1600), combined math+verbal. Toggleable with ACT via a switch button',
    detail: WHO_GETS_IN_STRINGS.charts.satScore.tooltip,
    data: {
      p25: (college.satmt25 ?? 0) + (college.satvr25 ?? 0),
      p50: (college.satmt50 ?? 0) + (college.satvr50 ?? 0),
      p75: (college.satmt75 ?? 0) + (college.satvr75 ?? 0),
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'ACT Score',
    display: 'Horizontal range bar chart (scale 1–36). Toggleable with SAT via a switch button',
    detail: WHO_GETS_IN_STRINGS.charts.actScore.tooltip,
    data: { p25: college.actcm25, p50: college.actcm50, p75: college.actcm75 },
  });

  useRegisterAgentContext(
    college.selectivity != null
      ? {
          kind: 'visual',
          label: 'Selectivity',
          display: 'Segmented bar chart showing selectivity level',
          detail: JSON.stringify(WHO_GETS_IN_STRINGS.selectivity.modal),
          data: { level: getSelectivityLabel(college.selectivity) },
        }
      : null,
  );

  useRegisterAgentContext(
    college.coursework_rigor != null
      ? {
          kind: 'visual',
          label: 'Coursework Rigor',
          display: 'Segmented bar chart showing importance level',
          detail: JSON.stringify(WHO_GETS_IN_STRINGS.charts.rigor.modal),
          data: { level: getRigorLabel(college.coursework_rigor) },
        }
      : null,
  );

  useRegisterAgentContext(
    college.extracurricular_involvement != null
      ? {
          kind: 'visual',
          label: 'Extracurriculars',
          display: 'Segmented bar chart showing involvement level',
          detail: JSON.stringify(WHO_GETS_IN_STRINGS.charts.extracurriculars.modal),
          data: { level: getExtracurricularsLabel(college.extracurricular_involvement) },
        }
      : null,
  );
};
